import { Grid, Typography } from '@mui/material';
import { memo, useCallback, useState } from 'react';

/** TRANSLATION */
import { useTranslation } from 'react-i18next';

/** COMPONENT */
import InputCheckbox from 'component/Input/InputCheckbox';
import InputTextField from 'component/Input/InputTextField';

/** UTILS */
import { numberFormat, numberToCurrencyFormat } from 'utils/app.utils';
import useWindowSize from 'utils/useWindowSize';

/** STYLE */
import { styled } from '@mui/material/styles';
import * as UseStyled from 'features/finance/payment/component/popupPaymentTemp/UseStyledFormPayment';
import _ from 'lodash';
import { ListOrderPayment } from './Index';
import { getItemType } from './utils/paymentUtils';

export const CustomInputCheckbox = styled(InputCheckbox)(({ theme }) => ({
  marginRight: 0,
  alignItems: 'flex-start',
  paddingTop: 9,
  '.MuiCheckbox-root': {
    marginTop: -7,
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    '.MuiCheckbox-root': {
      flex: 1
    },
    '.MuiFormControlLabel-label': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '100%',

    }
  },
}))

const initErrMsg = { OVER_MAX_PAID: '', OVER_BALANCE_PAID: '', }

type TreatmentListType = {
  systemConfig: {
    menu: 'PAYMENT' | 'OVERDUE' | 'RETROSPECTIVE' | 'EDIT_PAYMENT'
    type: 'TreatmentDiscount' | 'OtherDiscount' | 'PaymentChannels' | 'VOUCHER' | 'PROMOTION'
  }
  listOrder: any
  listOrderPayment: ListOrderPayment
  itemChannel: any

  readOnly?: boolean
  handleCheckBox: (type: string, id: any) => void
  handleValuePaidItem: (value: any, type: string, id: any) => void
  patientTreatmentRightId?: number
  // treatmentRight?: any
  // menu: 'PAYMENT' | 'OVERDUE' | 'RETROSPECTIVE' | 'EDIT_PAYMENT'
}

function TreatmentList(props: TreatmentListType) {
  const { t } = useTranslation()
  const [width] = useWindowSize()


  const [errMsg, setErrMsg] = useState({ ...initErrMsg })

  /**
   * รีเซ็ตข้อความ error
   */
  const clearErrMsg = useCallback(() => setErrMsg({ ...initErrMsg }), [])

  const renderBalance = useCallback((type: string, id: any) => {
    return numberFormat(props.listOrderPayment?.paid?.[`${type}_${id}`]?.balance || 0)
  }, [props.listOrderPayment])

  const getItem = useCallback((itemKey) => {
    const { treatmentRight: treatmentRightPayment, promotion, voucher } = props.listOrderPayment
    const patientTreatmentRightId = props?.patientTreatmentRightId
    const treatmentRight = treatmentRightPayment?.[patientTreatmentRightId ?? 0]

    const isTreatmentRight = patientTreatmentRightId
    let item = null
    if (isTreatmentRight) {

      if (props.systemConfig.type === 'PROMOTION') {
        item = Number(promotion?.promotionBranchId) === Number(patientTreatmentRightId) ? promotion?.itemChannel[itemKey] : null
      } else if (props.systemConfig.type === 'VOUCHER') {
        item = Number(voucher?.voucherListId) === Number(patientTreatmentRightId) ? voucher?.itemChannel[itemKey] : null
      } else
        item = treatmentRight?.itemChannel?.[itemKey]
    } else {
      item = props.itemChannel?.[itemKey]
    }

    return item

  }, [props?.patientTreatmentRightId, props.listOrderPayment, props.systemConfig.type, props.itemChannel])
  const renderValue = useCallback((type: string, id: any) => {
    const itemKey = `${type}_${id}`

    const item = getItem(itemKey)

    // Return the value if item exists, otherwise return 0
    // Don't check item?.checked here because we want to show the value even when typing
    return item?.value ?? 0
  }, [getItem])

  const renderDisabled = useCallback((type: string, id: any, key: string) => {
    const itemKey = `${type}_${id}`

    const isTreatmentRight = props?.patientTreatmentRightId
    if (isTreatmentRight && props.systemConfig.menu === 'EDIT_PAYMENT' && props.systemConfig.type !== 'TreatmentDiscount') return true

    const item = getItem(itemKey)
    return item?.[key] ?? false
  }, [getItem])

  const renderChecked = useCallback((type: string, id: any) => {
    const itemKey = `${type}_${id}`
    const item = getItem(itemKey)
    return item?.checked || false
  }, [getItem])

  /**
    * จัดการการระบุยอดเงินด้วยตัวเองสำหรับรายการนั้นๆ
    * ตรวจสอบความถูกต้องกับยอดคงเหลือและวงเงินสิทธิ์ก่อนอัปเดต Redux
    */
  const handleValuePaidItem = useCallback((valueCalculate: any, type: string, id: any) => {
    clearErrMsg()
    if (valueCalculate === '') valueCalculate = 0
    props.handleValuePaidItem(valueCalculate, type, id)
  }, [clearErrMsg, props.handleValuePaidItem, props.listOrderPayment])

  return (
    <>
      <Grid container spacing={2} className={'my-0'}>
        <Grid item sm={5} sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 500 }}>{t('PAYMENT.FORM_PAYMENT.TABLE.TREATMENT')}</Typography>
        </Grid>
        <Grid item sm={3} className={`d-none d-md-block`}>
          <Typography className='text-center' sx={{ fontWeight: 500 }}>{t('PAYMENT.FORM_PAYMENT.TABLE.BALANCE')}</Typography>
        </Grid>
        <Grid item sm={4} className={'pr-2'} sx={{ minWidth: '100px' }}>
          <Typography className={`${width < 768 ? 'text-right' : 'text-center'}`} sx={{ fontWeight: 500 }}>{t('PAYMENT.FORM_PAYMENT.TABLE.PRICE')}</Typography>
        </Grid>
      </Grid>
      {_.map(props.listOrder, (treatment, index: number) => {



        const { type, id } = getItemType(treatment, props.systemConfig.menu, index)
        const totalDcc = parseFloat(treatment.totalDcc) || 0
        const totalWithDcc = treatment.total + totalDcc

        return (
          <Grid container spacing={2} className={'my-0'}>
            <Grid item sm={5} sx={{ flex: 1 }} className={'overflow-hidden'}>
              {!props.readOnly && (
                <CustomInputCheckbox
                  label={`${width < 768 ? `[${'01-01'}]` : ''} ${treatment.operativeName || treatment.itemName} (${numberToCurrencyFormat(totalWithDcc)})`}
                  checked={renderChecked(type, id)}
                  onChange={(e) => { props.handleCheckBox(type, id) }}
                  disabled={renderDisabled(type, id, "disabledChecked")}
                />
              ) || (
                  <Typography>{width < 768 ? `01-01 ${treatment.operativeName || treatment.itemName}` : treatment.operativeName || treatment.itemName}</Typography>
                )}
              <Typography className='d-md-none' sx={{ margin: '-8px 0 0 28px' }}>({numberFormat(totalWithDcc)})</Typography>
            </Grid>
            <Grid item sm={3} className={`d-none d-md-block`}>
              <Typography className='text-center' sx={{ paddingTop: !props.readOnly ? '10px' : 0 }}>{renderBalance(type, id)} {t('CURRENCY_CHANGE.TH')}</Typography>
            </Grid>
            <Grid item sm={4} className={`${!props.readOnly ? 'pr-2' : ''}`} sx={{ minWidth: '100px' }}>
              <UseStyled.CustomField className='justify-content-end'>
                {!props.readOnly && (
                  <>
                    {/* Input Paid by item in channel */}
                    <InputTextField
                      disabled={renderDisabled(type, id, "disabledInput")}
                      value={renderValue(type, id)}
                      type='number'
                      financeText
                      hideControl
                      onchange={(e) => {
                        handleValuePaidItem(Number(String(e.target.value).replace(/[^0-9.]/g, '')), type, id)
                      }}
                      helperText={errMsg.OVER_MAX_PAID || ''}
                      inputTextAlign='right'
                    />
                    <Typography className='d-none d-md-block align-self-center ml-2'>{t('CURRENCY_CHANGE.TH')}</Typography>
                    {/* Input Paid by item in channel */}
                  </>
                ) || (
                    <Typography>{numberFormat(totalWithDcc)} {t('CURRENCY_CHANGE.TH')}</Typography>
                  )}
              </UseStyled.CustomField>
            </Grid>
          </Grid>
        )
      })}
    </>
  )
}

export default memo(TreatmentList)
